import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, IconButton, Pill, cx, useFocusTrap } from '@salonomy/ui';
import type { Notification } from '@salonomy/types';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../data/notifications.js';
import { KIND_LABEL, notificationLink, relativeTime } from './kinds.js';

/**
 * WS-14 — the NOTIFICATIONS INBOX drawer. A proper modal dialog (role=dialog, aria-modal, the shared
 * useFocusTrap, labelled by its heading) slid in from the right — mirrors the HelpDrawer shell. Lists
 * the salon's notifications newest-first (the API orders them) with a relative time, a per-item
 * mark-read, a mark-all-read action, an unread-only filter, an empty state, and — where a notification
 * carries an entity — a deep-link to the relevant console section.
 *
 * Read-state is SALON-WIDE: marking one (or all) read is a salon fact; the hooks invalidate the whole
 * tree so the badge and every open view re-read. Esc closes (the focus trap is the single source of
 * truth) and focus returns to the opener (the bell).
 *
 * The list itself is a PLAIN semantic list (<ul>/<li>, default list/listitem roles) — each row carries
 * a real <Link> (the title deep-link) and a 'Mark read' <button>, so it is NOT a single-select listbox
 * widget: those nested controls must stay first-class, AT-operable elements, which a leaf role=option
 * would flatten away. role=listbox/option is reserved for the keyboard-navigated CommandPalette leaf list.
 */

const TITLE_ID = 'notif-drawer-title';

export function NotificationsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Trap focus, close on Escape, restore focus to the opener (the bell) on close.
  useFocusTrap(panelRef, open, onClose);

  const { data, isLoading, isError } = useNotifications({ unreadOnly });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  if (!open) return null;

  const items = data?.data ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  return (
    <div className="notif-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        ref={panelRef}
        className="notif-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="notif-drawer__head">
          <div className="notif-drawer__heading">
            <h2 id={TITLE_ID} className="notif-drawer__title">
              Notifications
            </h2>
            {unreadCount > 0 ? (
              <Pill tone="cobalt">{unreadCount > 99 ? '99+' : unreadCount} unread</Pill>
            ) : null}
          </div>
          <IconButton label="Close notifications" onClick={onClose} className="notif-drawer__close">
            ×
          </IconButton>
        </header>

        <div className="notif-drawer__toolbar">
          <div className="notif-filter" role="group" aria-label="Filter notifications">
            <button
              type="button"
              className={cx('notif-filter__chip', !unreadOnly && 'notif-filter__chip--on')}
              aria-pressed={!unreadOnly}
              onClick={() => setUnreadOnly(false)}
            >
              All
            </button>
            <button
              type="button"
              className={cx('notif-filter__chip', unreadOnly && 'notif-filter__chip--on')}
              aria-pressed={unreadOnly}
              onClick={() => setUnreadOnly(true)}
            >
              Unread
            </button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAll.mutate()}
            disabled={unreadCount === 0 || markAll.isPending}
          >
            Mark all read
          </Button>
        </div>

        <div className="notif-drawer__body">
          {isLoading ? (
            <p className="notif-state" role="status">
              Loading…
            </p>
          ) : isError ? (
            <p className="notif-state notif-state--error" role="status">
              Couldn't load notifications. Try reopening.
            </p>
          ) : items.length === 0 ? (
            <p className="notif-state" role="status">
              {unreadOnly ? 'No unread notifications.' : "You're all caught up — nothing here yet."}
            </p>
          ) : (
            <ul className="notif-list" aria-label="Notifications">
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onMarkRead={() => markRead.mutate(n.id)}
                  onNavigate={onClose}
                  busy={markRead.isPending}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function NotificationRow({
  n,
  onMarkRead,
  onNavigate,
  busy,
}: {
  n: Notification;
  onMarkRead: () => void;
  onNavigate: () => void;
  busy: boolean;
}) {
  const unread = n.readAt == null;
  const to = notificationLink(n);

  const titleNode = to ? (
    <Link to={to} className="notif-item__title-link" onClick={onNavigate}>
      {n.title}
    </Link>
  ) : (
    <span className="notif-item__title">{n.title}</span>
  );

  return (
    <li className={cx('notif-item', unread && 'notif-item--unread')}>
      <div className="notif-item__main">
        <div className="notif-item__top">
          {unread ? <span className="notif-item__dot" aria-label="Unread" /> : null}
          {titleNode}
          <span className="notif-item__time" title={n.createdAt}>
            {relativeTime(n.createdAt)}
          </span>
        </div>
        {n.body ? <p className="notif-item__body">{n.body}</p> : null}
        <span className="notif-item__kind">{KIND_LABEL[n.kind]}</span>
      </div>
      {unread ? (
        <button
          type="button"
          className="notif-item__mark"
          onClick={onMarkRead}
          disabled={busy}
          aria-label="Mark read"
        >
          Mark read
        </button>
      ) : null}
    </li>
  );
}
